import { describeRpcError, isAuthorizationError } from "@how/app-kit";
import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PermissionDenied,
  useFocusWhenReady,
} from "@how/ui";
import { atCampus, useMyCampusFilter } from "../profile/campus-filter";
import {
  DAY_LABEL,
  formatMeetingTime,
  memberName,
  useDecideRequest,
  useFindGroups,
  useJoinGroup,
  useLeaveGroup,
  useLedRoster,
  type FoundGroup,
} from "./group-queries";
import { semanticColor } from "@how/design-system";

function notifyError(title: string, error: unknown): void {
  const described = describeRpcError(error);
  notifications.show({
    color: described.kind === "denied" ? "yellow" : "red",
    title,
    message: described.message,
  });
}

function meets(g: FoundGroup): string | null {
  const parts = [
    g.meetingDay === null ? null : DAY_LABEL[g.meetingDay],
    g.meetingTime === null ? null : `at ${formatMeetingTime(g.meetingTime) ?? ""}`,
    g.meetingLocation === null ? null : `· ${g.meetingLocation}`,
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? null : parts.join(" ");
}

const AGE_BANDS = [
  { value: "kids", label: "Children (under 12)", min: 0, max: 11 },
  { value: "youth", label: "Youth (12 to 17)", min: 12, max: 17 },
  { value: "adults", label: "Adults (18 and over)", min: 18, max: 120 },
] as const;

/** A group card's action in its new place, once joining, asking or leaving has moved it (issue #285). */
function actionOf(groupId: string, membership: "active" | "requested" | "none"): string {
  return `[data-group="${groupId}"][data-membership="${membership}"] [data-group-action]`;
}

/**
 * The member's half of groups (ADR 0040, task 5): my groups, the ones I lead (with requests to answer), and the
 * rest of what the church lists, with when and where each meets and a way in by its rule. Everything is resolved
 * from the login on the server; the screen sends a group id and, for a request, the member's own words. Joining,
 * asking and leaving move a group's card between sections; focus follows it there (issue #285).
 */
export function GroupsPage() {
  const groups = useFindGroups();
  const [day, setDay] = useState<string | null>(null);
  const [campus, setCampus] = useMyCampusFilter();
  const [category, setCategory] = useState<string | null>(null);
  const [age, setAge] = useState<string | null>(null);
  const [asking, setAsking] = useState<FoundGroup | null>(null);
  const [leaving, setLeaving] = useState<FoundGroup | null>(null);
  const focusWhenReady = useFocusWhenReady();

  const all = groups.data ?? [];
  const mine = all.filter((g) => g.mine?.status === "active" || g.mine?.status === "requested");
  const led = all.filter((g) => g.led);
  // Unlisted groups are reachable by their link, never listed (ADR 0040); the ones I am in are already above.
  const others = all.filter((g) => !mine.includes(g) && g.visibility !== "unlisted");
  const band = AGE_BANDS.find((b) => b.value === age);
  const listed = others.filter(
    (g) =>
      (day === null || g.meetingDay === day) &&
      atCampus(g.campus?.name, campus) &&
      (category === null || g.category === category) &&
      (band === undefined || ((g.ageMin ?? 0) <= band.max && (g.ageMax ?? 120) >= band.min)),
  );
  const options = (values: (string | null)[]) =>
    [...new Set(values.filter((v): v is string => v !== null))]
      .sort()
      .map((v) => ({ value: v, label: v }));

  return (
    <>
      <PageHeader
        title="Groups"
        description="Where people meet during the week. Join one, or ask to, and the leader hears from you."
      />
      {groups.isPending ? (
        <LoadingState rows={6} />
      ) : groups.isError ? (
        isAuthorizationError(groups.error) || describeRpcError(groups.error).kind === "denied" ? (
          <PermissionDenied />
        ) : (
          <ErrorState
            message="Could not load the groups."
            onRetry={() => {
              void groups.refetch();
            }}
          />
        )
      ) : (
        <Stack gap="xl">
          {mine.length > 0 ? (
            <section>
              <Title order={2} size="h3" mb="sm" tabIndex={-1} data-my-groups>
                My groups
              </Title>
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                {mine.map((g) => (
                  <GroupCard
                    key={g.id}
                    group={g}
                    onJoined={() => {
                      focusWhenReady(actionOf(g.id, "active"));
                    }}
                    onAsk={() => {
                      setAsking(g);
                    }}
                    onLeave={() => {
                      setLeaving(g);
                    }}
                  />
                ))}
              </SimpleGrid>
            </section>
          ) : null}

          {led.map((g) => (
            <LedRequests key={g.id} group={g} focusWhenReady={focusWhenReady} />
          ))}

          <section>
            <Title order={2} size="h3" mb="sm">
              {mine.length > 0 ? "Find another group" : "Find a group"}
            </Title>
            <Group gap="sm" mb="md" wrap="wrap">
              <Select
                aria-label="Day"
                placeholder="Any day"
                clearable
                data={(Object.keys(DAY_LABEL) as (keyof typeof DAY_LABEL)[])
                  .filter((d) => others.some((g) => g.meetingDay === d))
                  .map((d) => ({ value: d, label: DAY_LABEL[d] }))}
                value={day}
                onChange={setDay}
                w={170}
              />
              <Select
                aria-label="Campus"
                placeholder="Any campus"
                clearable
                data={options(others.map((g) => g.campus?.name ?? null))}
                value={campus}
                onChange={setCampus}
                w={200}
              />
              <Select
                aria-label="Kind"
                placeholder="Any kind"
                clearable
                data={options(others.map((g) => g.category))}
                value={category}
                onChange={setCategory}
                w={170}
              />
              <Select
                aria-label="Age"
                placeholder="Any age"
                clearable
                data={AGE_BANDS.map((b) => ({ value: b.value, label: b.label }))}
                value={age}
                onChange={setAge}
                w={200}
              />
            </Group>
            {listed.length === 0 ? (
              <EmptyState
                title={others.length === 0 ? "Nothing listed yet" : "Nothing matches"}
                description={
                  others.length === 0
                    ? "Your church has not listed any groups to join. Ask at the office about what is meeting."
                    : "Try fewer choices."
                }
              />
            ) : (
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                {listed.map((g) => (
                  <GroupCard
                    key={g.id}
                    group={g}
                    onJoined={() => {
                      focusWhenReady(actionOf(g.id, "active"));
                    }}
                    onAsk={() => {
                      setAsking(g);
                    }}
                    onLeave={() => {
                      setLeaving(g);
                    }}
                  />
                ))}
              </SimpleGrid>
            )}
          </section>
        </Stack>
      )}

      <AskModal
        group={asking}
        onClose={() => {
          setAsking(null);
        }}
        onAsked={(id) => {
          focusWhenReady(actionOf(id, "requested"));
        }}
      />
      <LeaveModal
        group={leaving}
        onClose={() => {
          setLeaving(null);
        }}
        onLeft={(id) => {
          focusWhenReady(actionOf(id, "none"));
        }}
      />
    </>
  );
}

function GroupCard({
  group: g,
  onAsk,
  onLeave,
  onJoined,
}: {
  group: FoundGroup;
  onAsk: () => void;
  onLeave: () => void;
  onJoined: () => void;
}) {
  const join = useJoinGroup();
  const status = g.mine?.status;
  const full = g.capacity !== null && g.activeMembers >= g.capacity;
  const when = meets(g);
  return (
    <Card
      data-group={g.id}
      data-membership={status === "active" || status === "requested" ? status : "none"}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Text fw={600}>{g.name}</Text>
            <Text size="xs" c="dimmed">
              {[g.category, g.campus?.name]
                .filter((p) => p !== null && p !== undefined)
                .join(" · ")}
            </Text>
          </div>
          {status === "active" ? (
            <Badge variant="light" color="teal">
              {g.mine?.role === "leader" ? "You lead" : "You are in"}
            </Badge>
          ) : status === "requested" ? (
            <Badge variant="light" color="yellow">
              Asked to join
            </Badge>
          ) : null}
        </Group>
        {when === null ? null : <Text size="sm">{when}</Text>}
        {g.description === null ? null : (
          <Text size="sm" c="dimmed">
            {g.description}
          </Text>
        )}
        {g.leaders.length > 0 ? (
          <Text size="xs" c="dimmed">
            Led by {g.leaders.join(" and ")}
          </Text>
        ) : null}
        {status === "active" ? (
          <Text size="xs" c="dimmed" data-testid="who-is-in-it">
            {g.membersConfidential
              ? "Members are confidential: only the leaders see who is in it."
              : g.members === null || g.members.length === 0
                ? null
                : `Who's in it: ${g.members.map((m) => (m.leader ? `${m.name} (leads)` : m.name)).join(", ")}`}
          </Text>
        ) : null}
        <Group justify="space-between" align="center" mt="xs">
          <Text size="xs" c="dimmed">
            {g.capacity === null
              ? `${String(g.activeMembers)} in the group`
              : `${String(g.activeMembers)} of ${String(g.capacity)} places taken`}
          </Text>
          {status === "active" || status === "requested" ? (
            <Button
              size="compact-sm"
              variant="subtle"
              color="gray"
              data-group-action
              aria-label={
                status === "requested"
                  ? `Withdraw your request to join ${g.name}`
                  : `Leave ${g.name}`
              }
              onClick={onLeave}
            >
              {status === "requested" ? "Withdraw" : "Leave"}
            </Button>
          ) : g.joiningRule === "closed" ? (
            <Text size="xs" c="dimmed">
              By invitation
            </Text>
          ) : g.joiningRule === "request" ? (
            <Button
              size="compact-sm"
              variant="light"
              data-group-action
              aria-label={`Ask to join ${g.name}`}
              onClick={onAsk}
            >
              Ask to join
            </Button>
          ) : full ? (
            <Text size="xs" c="dimmed">
              Full at the moment
            </Text>
          ) : (
            <Button
              size="compact-sm"
              loading={join.isPending}
              data-group-action
              aria-label={`Join ${g.name}`}
              onClick={() => {
                join.mutate(
                  { groupId: g.id, note: "" },
                  {
                    onSuccess: () => {
                      onJoined();
                      notifications.show({
                        color: semanticColor("success"),
                        title: "You are in",
                        message: `Welcome to ${g.name}.`,
                      });
                    },
                    onError: (error) => {
                      notifyError("Could not join", error);
                    },
                  },
                );
              }}
            >
              Join
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

/** Asking to join: the member's own words, read by the group's leaders (ADR 0040, owner decision 1). */
function AskModal({
  group,
  onClose,
  onAsked,
}: {
  group: FoundGroup | null;
  onClose: () => void;
  onAsked: (groupId: string) => void;
}) {
  const join = useJoinGroup();
  const [note, setNote] = useState("");
  return (
    <Modal
      opened={group !== null}
      onClose={onClose}
      title={group === null ? "" : `Ask to join ${group.name}`}
      radius="lg"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          A leader will see your request and answer it. You can say why you are asking, if you like.
        </Text>
        <Textarea
          label="A note for the leader"
          placeholder="I am new and looking for a group near me."
          value={note}
          maxLength={300}
          autosize
          minRows={2}
          onChange={(event) => {
            setNote(event.currentTarget.value);
          }}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={join.isPending}
            onClick={() => {
              if (group === null) return;
              join.mutate(
                { groupId: group.id, note },
                {
                  onSuccess: () => {
                    setNote("");
                    onClose();
                    onAsked(group.id);
                    notifications.show({
                      color: semanticColor("success"),
                      title: "Asked",
                      message: `The leaders of ${group.name} will see your request.`,
                    });
                  },
                  onError: (error) => {
                    notifyError("Could not send your request", error);
                  },
                },
              );
            }}
          >
            Send request
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function LeaveModal({
  group,
  onClose,
  onLeft,
}: {
  group: FoundGroup | null;
  onClose: () => void;
  onLeft: (groupId: string) => void;
}) {
  const leave = useLeaveGroup();
  const withdrawing = group?.mine?.status === "requested";
  return (
    <ConfirmActionModal
      opened={group !== null}
      onClose={onClose}
      title={withdrawing ? "Withdraw your request" : "Leave this group"}
      message={
        group === null
          ? ""
          : withdrawing
            ? `Withdraw your request to join ${group.name}? You can ask again later.`
            : `Leave ${group.name}? The leaders will see that you left. You can join again later.`
      }
      confirmLabel={withdrawing ? "Withdraw" : "Leave"}
      danger={!withdrawing}
      loading={leave.isPending}
      onConfirm={() => {
        if (group === null) return;
        leave.mutate(group.id, {
          onSuccess: () => {
            onClose();
            onLeft(group.id);
          },
          onError: (error) => {
            notifyError("Could not leave", error);
          },
        });
      }}
    />
  );
}

/** A leader answering requests from their phone: the same roster and decision staff use. */
function LedRequests({
  group,
  focusWhenReady,
}: {
  group: FoundGroup;
  focusWhenReady: ReturnType<typeof useFocusWhenReady>;
}) {
  const roster = useLedRoster(group.id);
  const decide = useDecideRequest();
  const requests = (roster.data?.members ?? []).filter((m) => m.status === "requested");
  // An answered request leaves the list: focus goes to the next one, or to My groups when none is left (issue #285).
  const decided = (membershipId: string) => {
    const index = requests.findIndex((m) => m.membershipId === membershipId);
    const next = requests[index + 1] ?? requests[index - 1];
    focusWhenReady(
      next === undefined ? "[data-my-groups]" : `[data-request="${next.membershipId}"] button`,
      { gone: `[data-request="${membershipId}"]` },
    );
  };
  if (roster.isPending || roster.isError || requests.length === 0) return null;
  return (
    <section>
      <Title order={2} size="h3" mb="sm">
        Asking to join {group.name}
      </Title>
      <Stack gap="xs">
        {requests.map((m) => (
          <Card key={m.membershipId} data-request={m.membershipId}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <div>
                <Text fw={600}>{memberName(m)}</Text>
                {m.requestNote === null ? null : (
                  <Text size="sm" fs="italic">
                    “{m.requestNote}”
                  </Text>
                )}
              </div>
              <Group gap="xs" wrap="nowrap">
                <Button
                  size="compact-sm"
                  variant="light"
                  color={semanticColor("success")}
                  loading={decide.isPending && decide.variables.membershipId === m.membershipId}
                  aria-label={`Approve ${memberName(m)}`}
                  onClick={() => {
                    decide.mutate(
                      { membershipId: m.membershipId, approve: true },
                      {
                        onSuccess: () => {
                          decided(m.membershipId);
                        },
                        onError: (error) => {
                          notifyError("Could not approve", error);
                        },
                      },
                    );
                  }}
                >
                  Approve
                </Button>
                <Button
                  size="compact-sm"
                  variant="subtle"
                  color="gray"
                  aria-label={`Decline ${memberName(m)}`}
                  onClick={() => {
                    decide.mutate(
                      { membershipId: m.membershipId, approve: false },
                      {
                        onSuccess: () => {
                          decided(m.membershipId);
                        },
                        onError: (error) => {
                          notifyError("Could not decline", error);
                        },
                      },
                    );
                  }}
                >
                  Decline
                </Button>
              </Group>
            </Group>
          </Card>
        ))}
      </Stack>
    </section>
  );
}
